<template>
    <glass-panel v-if="visible" padding="6" class="checklist">
        <div class="checklist__head">
            <div class="checklist__head-text">
                <h2 class="checklist__title">
                    <v-icon size="20" color="primary" class="mr-1">mdi-map-marker-path</v-icon>
                    Primeiros passos
                </h2>
                <span class="checklist__progress-label">{{ doneCount }} de {{ steps.length }}</span>
            </div>
            <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                    <v-btn icon small v-on="on" v-bind="attrs" aria-label="Ocultar primeiros passos" @click="dismiss">
                        <v-icon size="18">mdi-close</v-icon>
                    </v-btn>
                </template>
                <span>Ocultar este card</span>
            </v-tooltip>
        </div>

        <div class="checklist__bar" aria-hidden="true">
            <div class="checklist__bar-fill" :style="{ width: `${(doneCount / steps.length) * 100}%` }" />
        </div>

        <template v-if="!allDone">
            <div
                v-for="(step, i) in steps"
                :key="step.key"
                class="checklist__step"
                :class="{ 'checklist__step--done': step.done, 'checklist__step--next': i === nextIndex }"
            >
                <div class="checklist__step-icon">
                    <v-icon v-if="step.done" size="22" color="success">mdi-check-circle</v-icon>
                    <span v-else class="checklist__step-number">{{ i + 1 }}</span>
                </div>
                <div class="checklist__step-body">
                    <span class="checklist__step-title">{{ step.title }}</span>
                    <span class="checklist__step-desc">{{ step.description }}</span>
                </div>
                <v-btn
                    v-if="!step.done && i === nextIndex"
                    small
                    color="primary"
                    class="checklist__step-cta"
                    @click="$emit('go', step.go)"
                >
                    {{ step.cta }}
                </v-btn>
            </div>
        </template>

        <div v-else class="checklist__done">
            <v-icon size="28" color="success" class="mr-2">mdi-party-popper</v-icon>
            <div class="checklist__done-text">
                <strong>Tudo pronto!</strong>
                Seu site está publicando sozinho. Acompanhe tudo pelo calendário do piloto automático.
            </div>
            <v-btn small text color="primary" @click="dismiss">Ocultar</v-btn>
        </div>
    </glass-panel>
</template>

<script>
import Api from '@/lib/Api'

export default {
    name: 'ArtigosChecklist',
    props: {
        overview: { type: Object, required: true },
    },
    data() {
        return {
            dismissedLocal: false,
        }
    },
    computed: {
        counts() {
            return this.overview?.counts || {}
        },
        steps() {
            return [
                {
                    key: 'themes',
                    title: 'Escolha seus temas',
                    description: 'Diga quais ideias de temas você quer usar. Isso nos ensina o que combina com você.',
                    done: (this.counts.themes_classified || 0) >= 1,
                    cta: 'Ver temas',
                    go: 'criar',
                },
                {
                    key: 'article',
                    title: 'Crie seu 1º artigo',
                    description: 'Escolha um tema e veja a mágica acontecer. Você revisa antes de publicar.',
                    done: (this.counts.articles || 0) >= 1,
                    cta: 'Criar agora',
                    go: 'criar',
                },
                {
                    key: 'plan',
                    title: 'Ative o piloto automático',
                    description: 'Escolha os dias da semana e receba artigos prontos para aprovar por e-mail.',
                    done: (this.counts.schedules || 0) >= 1,
                    cta: 'Ativar',
                    go: 'calendario',
                },
            ]
        },
        doneCount() {
            return this.steps.filter(s => s.done).length
        },
        allDone() {
            return this.doneCount === this.steps.length
        },
        nextIndex() {
            return this.steps.findIndex(s => !s.done)
        },
        visible() {
            if (this.dismissedLocal) return false
            if (this.overview?.onboarding?.checklist_dismissed) return false
            return true
        },
    },
    methods: {
        dismiss() {
            this.dismissedLocal = true // otimista
            Api.updateArticlesOnboarding({ checklist_dismissed: true }).catch(() => {})
        },
    },
}
</script>

<style scoped>
.checklist__head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.checklist__head-text {
    flex: 1 1 auto;
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
}

.checklist__title {
    display: inline-flex;
    align-items: center;
    font-size: 17px;
    font-weight: 800;
    color: var(--color-on-surface, #2d2e37);
    margin: 0;
}

.checklist__progress-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant, #5b5b64);
}

.checklist__bar {
    height: 6px;
    border-radius: 9999px;
    background: rgba(112, 42, 225, 0.12);
    margin: 12px 0 18px;
    overflow: hidden;
}

.checklist__bar-fill {
    height: 100%;
    border-radius: 9999px;
    background: var(--color-primary, #702ae1);
    transition: width 400ms ease;
}

.checklist__step {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 14px;
    border-radius: var(--radius-sm, 12px);
    border: 1px solid transparent;
}

.checklist__step--next {
    border-color: var(--color-primary, #702ae1);
    background: rgba(112, 42, 225, 0.05);
}

.checklist__step--done {
    opacity: 0.75;
}

.checklist__step-icon {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.checklist__step-number {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    border: 2px solid var(--glass-panel-border, rgba(112, 105, 161, 0.3));
    color: var(--color-on-surface-variant, #5b5b64);
    font-size: 12.5px;
    font-weight: 800;
}

.checklist__step--next .checklist__step-number {
    border-color: var(--color-primary, #702ae1);
    color: var(--color-primary, #702ae1);
}

.checklist__step-body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.checklist__step-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--color-on-surface, #2d2e37);
}

.checklist__step--done .checklist__step-title {
    text-decoration: line-through;
    text-decoration-color: rgba(91, 91, 100, 0.5);
}

.checklist__step-desc {
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--color-on-surface-variant, #5b5b64);
}

.checklist__step-cta {
    flex-shrink: 0;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 700;
}

.checklist__done {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 4px 0;
}

.checklist__done-text {
    flex: 1 1 auto;
    font-size: 13.5px;
    color: var(--color-on-surface-variant, #5b5b64);
}

.checklist__done-text strong {
    color: var(--color-on-surface, #2d2e37);
    margin-right: 4px;
}

@media (max-width: 600px) {
    .checklist__step {
        flex-wrap: wrap;
    }

    .checklist__step-cta {
        margin-left: 44px;
    }
}
</style>
